import { defaultShouldDehydrateQuery, isServer, QueryClient } from "@tanstack/react-query"

import axios, { AxiosRequestConfig, InternalAxiosRequestConfig, AxiosInstance } from "axios"
import { logout } from "@/lib/http/auth"
import { store } from "@/lib/store"
import { ApiError } from "@/types/api"

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 6 * 60 * 1000, // 6 minutes
        refetchOnWindowFocus: false,
      },
      dehydrate: {
        shouldDehydrateQuery: (query) => defaultShouldDehydrateQuery(query) || query.state.status === "pending",
      },
    },
  })
}

export function parseApiError(error: ApiError, defaultMessage = "An unexpected error occurred."): string {
  const errorData = error?.response?.data?.error || error?.response?.data?.message

  if (typeof errorData === "string") {
    return errorData
  }

  if (typeof errorData === "object" && errorData !== null) {
    const errorValues = Object.values(errorData)
    if (Array.isArray(errorValues[0]) && typeof errorValues[0][0] === "string") {
      return errorValues[0][0]
    }
  }

  return defaultMessage
}

let browserQueryClient: QueryClient | undefined = undefined

export function getQueryClient(): QueryClient {
  if (isServer) {
    return createQueryClient()
  }
  if (!browserQueryClient) browserQueryClient = createQueryClient()
  return browserQueryClient
}

function createHttpClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: (import.meta.env.VITE_API_URL || "") + "/v1",
    withCredentials: false,
    timeout: 90000,
    headers: {
      "Content-Type": "application/json",
    },
  })

  instance.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      if (!config.headers) config.headers = {}
      const internalConfig = config as InternalAxiosRequestConfig<unknown>
      const token = store.getState().auth.token
      if (token) {
        internalConfig.headers["Authorization"] = `Bearer ${token}`
      }
      return internalConfig
    },
    (error) => Promise.reject(error)
  )

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!error.response) return Promise.reject(error)

      if (error.response.data) {
        error.response.data.error = parseApiError(error)
      }

      switch (error.response.status) {
        case 401:
          await logout()
          break
        case 404:
        case 422:
        case 429:
          // Pass through
          break
        default:
          break
      }
      return Promise.reject(error)
    }
  )

  return instance
}

const http = createHttpClient()

export default http
